// src/components/SidebarLayout.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Menu,
  Wand2,
  Lightbulb,
  CreditCard,
  Sparkles,
  Image as ImageIcon,
  Film,
  Link as LinkIcon,
  Loader2,
  ChevronRight,
  ChevronDown,
  Info,
  X,
} from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useAuth } from "@clerk/clerk-react";
import axios from "axios";

const HEADER_H = "72px"; // keep in sync with Header.jsx (h-[72px])
const PROMO_H_VAR = "var(--pixelads-promo-h, 0px)";
const TOP_OFFSET = `calc(${HEADER_H} + ${PROMO_H_VAR})`;

/**
 * ✅ Z-INDEX RULES (keeps modals on top, and sidebar dimmed under overlay)
 * - Most app modals use z-50 (overlay) and above.
 * - Sidebar MUST be below that so it gets covered/dimmed.
 */
const Z_SIDEBAR = 30; // desktop sidebar
const Z_MOBILE_SIDEBAR = 30; // mobile sidebar backdrop/panel
const Z_MOBILE_TOGGLE = 35; // mobile hamburger (still below modals)

/* ------------------------------ helpers ------------------------------ */

function formatTs(tsSec) {
  if (!tsSec) return "";
  const d = new Date(tsSec * 1000);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Robust Euro parser for add-on labels */
const parseEuroFromLabel = (label) => {
  if (!label) return null;
  const m = label.match(/€\s*([\d.,]+)/);
  if (!m) return null;
  const s = m[1].trim();

  // If both '.' and ',' → European: '.' thousands, ',' decimals
  if (s.includes(".") && s.includes(",")) {
    return Number.parseFloat(s.replace(/\./g, "").replace(",", "."));
  }
  // Only comma → decimal separator
  if (s.includes(",")) {
    return Number.parseFloat(s.replace(",", "."));
  }
  // Only dot (or digits) → parse directly
  return Number.parseFloat(s);
};

/**
 * ✅ Quota normalization (ignore analyzed/input assets)
 * Some quota endpoints may include "input/analyzed" assets in `used`.
 * Upscaler quota never counts them; replicate that behavior here by subtracting
 * any input-used breakdown fields when present, and recomputing remaining.
 *
 * NOTE: This is a no-op unless the backend includes one of these fields.
 */
function adjustQuotaToIgnoreInputs(q) {
  if (!q || typeof q !== "object") return q;

  const n = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? num : 0;
  };

  // look for any commonly-used input/analyzed breakdown fields
  const inputUsedRaw =
    q.inputUsed ??
    q.inputsUsed ??
    q.used_input ??
    q.usedInputs ??
    q.used_input_assets ??
    q.usedInputAssets ??
    q.usedAnalyzed ??
    q.analyzedUsed ??
    q.used_analyzed ??
    q.usedAnalyzedAssets ??
    q.breakdown?.input ??
    q.breakdown?.inputs ??
    q.breakdown?.analyzed ??
    q.breakdown?.input_assets ??
    null;

  if (inputUsedRaw == null) return q;

  const used = n(q.used);
  const inputUsed = n(inputUsedRaw);
  if (inputUsed <= 0) return q;

  const baseCap = n(q.baseCap);
  const extra =
    q.imagesExtra != null
      ? n(q.imagesExtra)
      : q.videosExtra != null
      ? n(q.videosExtra)
      : q.urlsExtra != null
      ? n(q.urlsExtra)
      : 0;

  const totalCap = baseCap + extra;
  const usedAdj = Math.max(0, used - inputUsed);

  // keep unlimited/blocked flags intact; only recompute remaining when not unlimited
  const unlimited = Boolean(q.unlimited);
  const remainingAdj = unlimited ? q.remaining : Math.max(0, totalCap - usedAdj);

  return {
    ...q,
    used: usedAdj,
    remaining: remainingAdj,
  };
}

/* -------------------------- Add-ons Modal (Sidebar) ---------------------- */

function QtySelect({ value, onChange, disabled }) {
  return (
    <div className="relative w-full">
      <select
        value={value}
        disabled={disabled}
        onChange={(e) =>
          onChange(Math.min(50, Math.max(1, Number(e.target.value) || 1)))
        }
        className="appearance-none rounded-full pl-3 pr-7 py-1.5 bg-[#0f0f21] text-white border w-full text-xs cursor-pointer"
        style={{ borderColor: "#2A2A40" }}
        aria-label="Quantity"
      >
        {Array.from({ length: 50 }, (_, i) => i + 1).map((n2) => (
          <option key={n2} value={n2}>
            {n2}
          </option>
        ))}
      </select>
      <ChevronDown
        size={13}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-white/50"
      />
    </div>
  );
}

function SidebarAddonsModal({ open, onClose }) {
  const { getToken, isSignedIn } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [addons, setAddons] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [buttonLoading, setButtonLoading] = useState(null);
  const [addonQty, setAddonQty] = useState({});

  const getQty = (priceId) => Math.max(1, addonQty[priceId] || 1);
  const setQty = (priceId, q) =>
    setAddonQty((s) => ({
      ...s,
      [priceId]: Math.min(50, Math.max(1, q | 0)),
    }));

  const load = useCallback(async () => {
    if (!isSignedIn) return;
    try {
      setLoading(true);
      setError("");
      const token = await getToken();
      const baseUrl = process.env.REACT_APP_BACKEND_URL;

      const [addonsRes, statusRes] = await Promise.all([
        axios.get(`${baseUrl}/api/billing/topups`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }),
        axios
          .get(`${baseUrl}/api/billing/status`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          })
          .catch(() => null),
      ]);

      setAddons(
        (addonsRes?.data?.topups || []).filter((t) =>
          (t.type || "").startsWith("addon")
        )
      );
      setStatus(statusRes?.data || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to load sidebar add-ons:", e?.message || e);
      setError("Unable to load add-ons right now.");
    } finally {
      setLoading(false);
    }
  }, [getToken, isSignedIn]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const goCheckout = useCallback(
    async ({ priceId, quantity = 1 }) => {
      try {
        setButtonLoading(priceId);
        const token = await getToken();

        const currentUrl =
          typeof window !== "undefined" ? window.location.href : undefined;

        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/billing/checkout`,
          {
            mode: "payment",
            priceId,
            quantity,
            successUrl: currentUrl,
            cancelUrl: currentUrl,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          }
        );

        if (onClose) onClose();
        window.location.href = res.data.url;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          "Sidebar add-on checkout failed:",
          e?.response?.data || e?.message || e
        );
        setButtonLoading(null);
        // eslint-disable-next-line no-alert
        alert(
          e?.response?.data?.error ||
            "Checkout failed. Please try again from the Pricing page."
        );
      }
    },
    [getToken, onClose]
  );

  const goPricing = useCallback(() => {
    if (onClose) onClose();
    navigate("/pricing");
  }, [navigate, onClose]);

  if (!open) return null;

  const unlimited = Boolean(status?.unlimited?.isUnlimited);
  const hasSubscription = Boolean(status?.currentPriceId);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 px-4">
      <div
        className="w-full max-w-lg rounded-2xl bg-[#15152B] border border-[#2A2A40] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A40]">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-white">
              Need a bit more this month?
            </span>
            <span className="text-[11px] text-white/60">
              One-off add-ons. Applied instantly after checkout.
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/10 text-white/80"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3 text-xs text-white/80">
          {unlimited ? (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-900/30 border border-emerald-500/40 px-3 py-2 text-[11px]">
              <Sparkles size={14} className="text-emerald-300" />
              <span>
                Unlimited access is enabled for this account. Add-ons are not
                needed and payments are disabled.
              </span>
            </div>
          ) : !hasSubscription ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 rounded-lg bg-amber-900/30 border border-amber-500/40 px-3 py-2 text-[11px]">
                <Info size={14} className="text-amber-300" />
                <span>
                  Start a subscription first, then you can add extra images,
                  videos or pages on top.
                </span>
              </div>

              {/* ✅ CTA only when there is NO active tier/subscription */}
              <button
                type="button"
                onClick={goPricing}
                className="w-full inline-flex items-center justify-between rounded-xl px-3 py-2.5 text-[11px] font-semibold transition hover:-translate-y-[1px] hover:shadow-xl"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(248,46,136,0.18) 0%, rgba(20,20,40,0.96) 100%)",
                  border: "1px solid rgba(248,46,136,0.55)",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.6)",
                  color: "#FFFFFF",
                }}
              >
                <span className="flex items-center gap-2">
                  <CreditCard size={14} className="opacity-90" />
                  View plans on Pricing
                </span>
                <ChevronRight size={14} className="opacity-90" />
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="flex items-center gap-2 text-[11px] text-white/70">
              <Loader2 size={14} className="animate-spin" />
              Loading add-ons…
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-[11px] text-[#f97373]">
              <Info size={14} />
              {error}
            </div>
          ) : addons.length === 0 ? (
            <div className="text-[11px] text-white/60">
              No add-ons are available right now.
            </div>
          ) : (
            <div className="space-y-3">
              {addons.map((t) => {
                const sub =
                  t.id === "url_1"
                    ? "+1 Product Page"
                    : t.id === "video_1"
                    ? "+1 video"
                    : t.id === "images_10"
                    ? "+10 images"
                    : "";

                const unitPrice = t.unitPrice ?? parseEuroFromLabel(t.label);
                const qty = getQty(t.priceId);
                const disabled =
                  !hasSubscription || unlimited || buttonLoading === t.priceId;

                const euro2 = (n2) =>
                  new Intl.NumberFormat(undefined, {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(n2);

                const totalStr =
                  unitPrice != null ? euro2(unitPrice * qty) : null;

                const resultStr =
                  t.id === "url_1"
                    ? `${qty} Product Page${qty > 1 ? "s" : ""}`
                    : t.id === "images_10"
                    ? `${qty * 10} images`
                    : t.id === "video_1"
                    ? `${qty} video${qty > 1 ? "s" : ""}`
                    : `${qty} unit${qty > 1 ? "s" : ""}`;

                const isImagesAddon =
                  t.id === "images_10" ||
                  (t.type || "").toLowerCase().includes("images");
                const isVideoAddon =
                  t.id === "video_1" ||
                  (t.type || "").toLowerCase().includes("video");
                const isUrlAddon =
                  t.id === "url_1" ||
                  (t.type || "").toLowerCase().includes("url");

                let TypeIcon = null;
                if (isImagesAddon) TypeIcon = ImageIcon;
                else if (isVideoAddon) TypeIcon = Film;
                else if (isUrlAddon) TypeIcon = LinkIcon;

                return (
                  <div
                    key={t.id}
                    className="rounded-xl border border-[#2A2A40] bg-[#141428] px-3 py-3"
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0">
                          {TypeIcon && (
                            <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#111827] border border-white/5 text-white/80">
                              <TypeIcon size={14} />
                            </span>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-white truncate">
                              {t.label}
                            </div>
                            <div className="text-[11px] text-white/60">
                              {sub}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 flex flex-col gap-1.5">
                        <div className="text-[11px] text-white/60">Quantity</div>
                        <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.9fr)_auto] gap-2 items-center">
                          <QtySelect
                            value={qty}
                            disabled={disabled}
                            onChange={(v) => setQty(t.priceId, v)}
                          />

                          <div className="flex items-center">
                            {totalStr && (
                              <div
                                className="rounded-full px-3 py-1.5 text-[11px] text-white w-full text-center md:text-left"
                                style={{
                                  background:
                                    "linear-gradient(180deg, rgba(248,46,136,0.16) 0%, rgba(20,20,40,0.96) 100%)",
                                  border: "1px solid rgba(248,46,136,0.45)",
                                }}
                              >
                                You’ll get{" "}
                                <span className="font-semibold">{resultStr}</span>{" "}
                                for{" "}
                                <span className="font-semibold">{totalStr}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex justify-end">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed transition hover:translate-y-[-1px]"
                              style={{
                                background: "#22c55e",
                                boxShadow: "0 8px 20px rgba(34,197,94,0.35)",
                              }}
                              disabled={disabled}
                              onClick={() =>
                                goCheckout({
                                  priceId: t.priceId,
                                  quantity: qty,
                                })
                              }
                            >
                              {buttonLoading === t.priceId ? (
                                <>
                                  <Loader2 size={13} className="animate-spin" />
                                  Processing…
                                </>
                              ) : (
                                <>
                                  Buy
                                  <ChevronRight size={13} className="opacity-80" />
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pt-1 text-[10px] text-white/40">
            Stripe handles the payment. Limits are updated as soon as the checkout
            succeeds.
          </div>
        </div>
      </div>

      <button
        type="button"
        className="fixed inset-0 z-[-1] cursor-default"
        onClick={onClose}
        aria-hidden="true"
      />
    </div>
  );
}

function SidebarBillingSummary({ onOpenAddons }) {
  const { getToken, isSignedIn } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [addonsHover, setAddonsHover] = useState(false);

  const [imagesQuota, setImagesQuota] = useState(null);
  const [videosQuota, setVideosQuota] = useState(null);
  const [urlsQuota, setUrlsQuota] = useState(null);

  const load = useCallback(async () => {
    if (!isSignedIn) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const token = await getToken();
      const baseUrl = process.env.REACT_APP_BACKEND_URL;

      const [statusRes, imagesRes, videosRes, urlsRes] = await Promise.all([
        axios
          .get(`${baseUrl}/api/billing/status`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          })
          .catch(() => null),
        axios
          .get(`${baseUrl}/api/product-avatar-images/quota`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => null),
        axios
          .get(`${baseUrl}/api/product-avatar-video/quota`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => null),
        axios
          .get(`${baseUrl}/api/analyze/quota`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          })
          .catch(() => null),
      ]);

      setStatus(statusRes?.data || null);

      // ✅ Apply same behavior as Upscaler quotas: never count analyzed/input assets (when breakdown exists)
      setImagesQuota(adjustQuotaToIgnoreInputs(imagesRes?.data || null));
      setVideosQuota(adjustQuotaToIgnoreInputs(videosRes?.data || null));
      setUrlsQuota(adjustQuotaToIgnoreInputs(urlsRes?.data || null));

      if (!statusRes && !imagesRes && !videosRes && !urlsRes) {
        setError("Unable to load plan details right now.");
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to load sidebar billing status:", e?.message || e);
      setError("Unable to load plan details right now.");
    } finally {
      setLoading(false);
    }
  }, [getToken, isSignedIn]);

  useEffect(() => {
    load();
  }, [load]);

  // ✅ Refresh quotas after successful generation submits (interceptor, sidebar-only)
  const lastRefreshAtRef = useRef(0);

  useEffect(() => {
    if (!isSignedIn) return undefined;

    const id = axios.interceptors.response.use(
      (res) => {
        try {
          const method = (res?.config?.method || "").toLowerCase();
          const url = String(res?.config?.url || "");
          const data = res?.data;

          // Ignore billing/quota calls to prevent loops
          const isBilling = url.includes("/api/billing/");
          const isQuota = url.includes("/quota");

          // Heuristics: generation endpoints often use /generate or /submit
          const looksLikeGenerationEndpoint =
            url.includes("/generate") ||
            url.includes("/submit") ||
            url.includes("/task/submit");

          // ✅ Also refresh after analyzing a URL / creating a project from URL
          const looksLikeAnalyzeEndpoint =
            url.includes("/api/analyze") ||
            url.includes("/api/projects/analyze") ||
            (url.includes("/api/projects") && url.includes("/analyze"));

          // Heuristics: queued-like responses
          const looksLikeQueuedResponse =
            Boolean(data?.assetId) ||
            Boolean(data?.jobId) ||
            data?.status === "queued" ||
            data?.queued === true;

          // Heuristics: analyze-like responses (project created / analyzed)
          const looksLikeAnalyzeResponse =
            Boolean(data?.projectId) || Boolean(data?.id) || data?.ok === true;

          if (
            method === "post" &&
            !isBilling &&
            !isQuota &&
            (looksLikeGenerationEndpoint ||
              looksLikeQueuedResponse ||
              looksLikeAnalyzeEndpoint ||
              looksLikeAnalyzeResponse)
          ) {
            const now = Date.now();
            // throttle to avoid spamming refreshes
            if (now - lastRefreshAtRef.current > 1200) {
              lastRefreshAtRef.current = now;
              load();
            }
          }
        } catch {
          // no-op
        }
        return res;
      },
      (err) => Promise.reject(err)
    );

    return () => {
      axios.interceptors.response.eject(id);
    };
  }, [isSignedIn, load]);

  if (!isSignedIn) return null;

  const unlimited = Boolean(status?.unlimited?.isUnlimited);

  // ✅ Prefer billing/status tier + addons breakdown for sidebar display
  const tier = status?.tier || null; // { caps, usage, remaining, ... }
  const addonsRemaining = status?.addons?.remaining || {}; // { images, videos, urls }

  const n = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? num : 0;
  };

  const statusTierRemImages =
    tier?.remaining?.images != null ? n(tier.remaining.images) : null;
  const statusTierRemVideos =
    tier?.remaining?.videos != null ? n(tier.remaining.videos) : null;
  const statusTierRemUrls =
    tier?.remaining?.urls != null ? n(tier.remaining.urls) : null;

  // Fallback (feature quota endpoints)
  let fallbackImagesLeft = null;
  let totalImagesLeft = null;
  if (!unlimited && imagesQuota) {
    const cap = (imagesQuota.baseCap || 0) + (imagesQuota.imagesExtra || 0);
    if (cap > 0) {
      fallbackImagesLeft = imagesQuota.remaining ?? 0;
      totalImagesLeft = imagesQuota.remaining ?? 0;
    }
  }

  let fallbackVideosLeft = null;
  let totalVideosLeft = null;
  if (!unlimited && videosQuota) {
    const cap = (videosQuota.baseCap || 0) + (videosQuota.videosExtra || 0);
    if (cap > 0) {
      fallbackVideosLeft = videosQuota.remaining ?? 0;
      totalVideosLeft = videosQuota.remaining ?? 0;
    }
  }

  let fallbackUrlsLeft = null;
  let totalUrlsLeft = null;
  if (!unlimited && urlsQuota) {
    const cap = (urlsQuota.baseCap || 0) + (urlsQuota.urlsExtra || 0);
    if (cap > 0) {
      fallbackUrlsLeft = urlsQuota.remaining ?? 0;
      totalUrlsLeft = urlsQuota.remaining ?? 0;
    }
  }

  const tierImagesLeft =
    unlimited
      ? "∞"
      : statusTierRemImages != null
      ? statusTierRemImages
      : fallbackImagesLeft;
  const tierVideosLeft =
    unlimited
      ? "∞"
      : statusTierRemVideos != null
      ? statusTierRemVideos
      : fallbackVideosLeft;
  const tierUrlsLeft =
    unlimited
      ? "∞"
      : statusTierRemUrls != null
      ? statusTierRemUrls
      : fallbackUrlsLeft;

  // ✅ Fix: derive add-ons remaining from the quota endpoints (true remaining),
  // instead of trusting billing/status which can be "caps" not "remaining".
  const statusAddonRemImages =
    addonsRemaining?.images != null ? n(addonsRemaining.images) : 0;
  const statusAddonRemVideos =
    addonsRemaining?.videos != null ? n(addonsRemaining.videos) : 0;
  const statusAddonRemUrls =
    addonsRemaining?.urls != null ? n(addonsRemaining.urls) : 0;

  const addonRemImages =
    unlimited
      ? statusAddonRemImages
      : totalImagesLeft != null
      ? Math.max(0, n(totalImagesLeft) - n(statusTierRemImages ?? 0))
      : statusAddonRemImages;

  const addonRemVideos =
    unlimited
      ? statusAddonRemVideos
      : totalVideosLeft != null
      ? Math.max(0, n(totalVideosLeft) - n(statusTierRemVideos ?? 0))
      : statusAddonRemVideos;

  const addonRemUrls =
    unlimited
      ? statusAddonRemUrls
      : totalUrlsLeft != null
      ? Math.max(0, n(totalUrlsLeft) - n(statusTierRemUrls ?? 0))
      : statusAddonRemUrls;

  const sub = status?.activeSubscription || {};
  const subStatus = sub.status;
  const cancelAtPeriodEnd = Boolean(sub.cancel_at_period_end);
  const renewLabel = sub.current_period_end ? formatTs(sub.current_period_end) : "";

  const tierLabel =
    status?.planName ||
    status?.currentPlanName ||
    status?.currentTier ||
    sub.plan_nickname ||
    sub.plan?.nickname ||
    (status?.currentPriceId ? "Paid plan" : "Free plan");

  const StatTile = ({ icon: Icon, label, value, highlight }) => (
    <div className="flex flex-col items-center text-center min-w-0">
      <Icon size={18} className="text-white/70" />
      <div className="mt-1 text-[10px] font-semibold text-white/55 leading-none">
        {label}
      </div>
      <div
        className="mt-2 w-full rounded-2xl border px-2 py-3 flex items-center justify-center"
        style={{
          background: highlight
            ? "linear-gradient(180deg, rgba(248,46,136,0.10) 0%, rgba(20,20,40,0.85) 100%)"
            : "#0f0f21",
          borderColor: highlight ? "rgba(248,46,136,0.45)" : "#2A2A40",
          boxShadow: highlight
            ? "0 0 0 1px rgba(248,46,136,0.20) inset"
            : "inset 0 1px 0 rgba(255,255,255,0.03)",
        }}
      >
        <div className="text-[22px] font-extrabold tracking-tight text-white leading-none">
          {value == null ? "—" : value}
        </div>
      </div>
    </div>
  );

  const Section = ({ title, children }) => (
    <div
      className="rounded-2xl border p-3"
      style={{
        background: "#141428",
        borderColor: "#2A2A40",
      }}
    >
      <div className="text-[10px] uppercase tracking-wide text-white/45">
        {title}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">{children}</div>
    </div>
  );

  return (
    <div className="text-xs space-y-3">
      {/* Plan header */}
      <div className="rounded-2xl bg-[#15152B] border border-[#2A2A40] p-4">
        <div className="text-[11px] uppercase tracking-wide text-white/45">
          Plan
        </div>

        <div className="mt-2">
          <div className="text-lg font-semibold text-white leading-tight truncate">
            {unlimited ? "Unlimited" : tierLabel || "No active subscription"}
          </div>

          {unlimited ? (
            <div className="mt-1 text-[11px] text-emerald-300">
              Stripe billing disabled
            </div>
          ) : subStatus ? (
            <div className="mt-1 text-[11px] text-white/60">
              {cancelAtPeriodEnd
                ? renewLabel
                  ? `Cancels on ${renewLabel}`
                  : "Cancels at period end"
                : renewLabel
                ? `Renews on ${renewLabel}`
                : `Status: ${subStatus}`}
            </div>
          ) : (
            <div className="mt-1 text-[11px] text-white/60">
              You&apos;re on a free plan.
            </div>
          )}
        </div>

        {/* Usage */}
        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-white/60 text-[11px]">
              <Loader2 size={14} className="animate-spin" />
              Loading…
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-[#f97373] text-[11px]">
              <Info size={13} />
              {error}
            </div>
          ) : (
            <>
              <Section title="Tier">
                <StatTile icon={ImageIcon} label="Images" value={tierImagesLeft} />
                <StatTile
                  icon={Film}
                  label="Videos"
                  value={tierVideosLeft}
                  highlight
                />
                <StatTile icon={LinkIcon} label="Pages" value={tierUrlsLeft} />
              </Section>

              {!unlimited && (
                <Section title="Add-ons">
                  <StatTile icon={ImageIcon} label="Images" value={addonRemImages} />
                  <StatTile
                    icon={Film}
                    label="Videos"
                    value={addonRemVideos}
                    highlight
                  />
                  <StatTile icon={LinkIcon} label="Pages" value={addonRemUrls} />
                </Section>
              )}
            </>
          )}
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={onOpenAddons}
          onMouseEnter={() => setAddonsHover(true)}
          onMouseLeave={() => setAddonsHover(false)}
          className="mt-3 relative inline-flex items-center justify-between w-full rounded-2xl pl-10 pr-3 py-2.5 text-[11px] font-semibold transition hover:-translate-y-[1px] hover:shadow-xl"
          style={{
            background: addonsHover
              ? "#FFFFFF"
              : "linear-gradient(180deg, rgba(248,46,136,0.16) 0%, rgba(20,20,40,0.96) 100%)",
            border: addonsHover
              ? "1px solid #FFFFFF"
              : "1px solid rgba(248,46,136,0.45)",
            boxShadow: "0 12px 30px rgba(0,0,0,0.65)",
            color: addonsHover ? "#FC2783" : "#FFFFFF",
          }}
        >
          <span className="pointer-events-none absolute -top-2 -left-1">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-yellow-300 bg-[#111827] shadow-lg">
              <Sparkles size={16} className="text-yellow-300" />
            </span>
          </span>

          <span className="flex flex-col leading-tight text-left">
            <span>Need more this month?</span>
            <span className="text-[10px] text-inherit">Buy add-ons</span>
          </span>

          <ChevronRight size={12} className="opacity-90" />
        </button>
      </div>
    </div>
  );
}

export default function SidebarLayout({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [addonsOpen, setAddonsOpen] = useState(false);

  const navLinks = [
    { to: "/analyze", label: "Products", icon: <Lightbulb size={20} /> },
  ];

  const footerLinks = [{ to: "/pricing", label: "Billing", icon: <CreditCard size={20} /> }];

  const renderNavLinks = (links) =>
    links.map(({ to, label, icon }) => (
      <Tooltip.Root key={to} delayDuration={300}>
        <Tooltip.Trigger asChild>
          <NavLink to={to} onClick={() => setIsOpen(false)} className="block">
            {({ isActive }) => (
              <div
                className={[
                  "relative group flex items-center gap-3 px-5 py-3 rounded-xl font-semibold overflow-hidden w-full cursor-pointer",
                  "transition-all duration-300 ease-out",
                  isActive
                    ? "text-[#FC2783]"
                    : "text-white/80 hover:text-[#FC2783] hover:-translate-x-[2px] hover:drop-shadow-[0_0_8px_rgba(252,39,131,0.25)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FC2783]/40",
                ].join(" ")}
              >
                {isActive && (
                  <>
                    <span className="pointer-events-none absolute left-0 top-1/2 h-6 w-[4px] -translate-y-1/2 rounded-full bg-[#FC2783]" />
                    <span className="pointer-events-none absolute right-0 top-1/2 h-6 w-[4px] -translate-y-1/2 rounded-full bg-[#FC2783]" />
                  </>
                )}
                <span className="transition-transform duration-300 ease-out group-hover:translate-x-[2px]">
                  {icon}
                </span>
                <span className="transition-all duration-300 ease-out group-hover:tracking-tight">
                  {label}
                </span>
              </div>
            )}
          </NavLink>
        </Tooltip.Trigger>
      </Tooltip.Root>
    ));

  return (
    <div className="flex min-h-screen bg-[#0F0F1B] text-white">
      {/* Desktop Sidebar — start BELOW promo + header */}
      <aside
        className="hidden md:flex flex-col w-[260px] transition-all duration-300 bg-[#15152B] sticky border-r border-[#2A2A40] rounded-r-3xl shadow-lg shadow-black/30 overflow-hidden"
        style={{
          top: TOP_OFFSET,
          height: `calc(100vh - ${HEADER_H} - ${PROMO_H_VAR})`,
          zIndex: Z_SIDEBAR, // ✅ below modals (so overlay dims sidebar)
        }}
      >
        <nav className="px-2 pt-4 pb-1 space-y-1">
          <NavLink
            to="/generate"
            end
            onClick={() => setIsOpen(false)}
            className={({ isActive }) =>
              [
                "group relative flex items-center gap-3 px-5 py-3 rounded-xl font-semibold w-full cursor-pointer overflow-hidden",
                "transition-colors duration-300.ease-out will-change-auto",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FC2783]/40",
                isActive
                  ? "bg-pink-600 text-white"
                  : "bg-[#FC2783] text-white hover:bg-white hover:text-[#FC2783] hover:-translate-y-[1px]",
              ].join(" ")
            }
          >
            <span className="relative transition-transform duration-300 ease-out group-hover:scale-105">
              <Wand2 size={20} />
            </span>
            <span className="relative transition-all duration-300.ease-out group-hover:tracking-tight">
              Generate
            </span>
          </NavLink>
        </nav>

        <nav className="flex-1 px-2 pt-1 pb-1 space-y-1">
          {renderNavLinks(navLinks)}

          <Tooltip.Root delayDuration={300}>
            <Tooltip.Trigger asChild>
              <div
                className={[
                  "relative group px-5 py-3 w-full cursor-not-allowed",
                  "text-white/40 hover:text-[#FC2783]",
                  "transition-all duration-300 ease-out hover:-translate-x-[2px] hover:drop-shadow-[0_0_10px_rgba(37,99,235,0.45)]",
                ].join(" ")}
                aria-disabled="true"
              >
                <div className="flex items-center gap-3">
                  <span className="transition-transform duration-300 ease-out group-hover:translate-x-[2px]">
                    <Sparkles size={20} className="opacity-80" />
                  </span>
                  <span className="font-semibold">Winning ads</span>
                </div>
                <div className="mt-2 ml-7 inline-flex items-center gap-1.5 rounded-full border border-blue-400/70 bg-blue-500/15 px-2.5 py-0.5 text-[11px] text-blue-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  <span>Coming soon</span>
                </div>
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="right"
                sideOffset={8}
                className="rounded-xl bg-black/90 text-white text-xs px-3 py-2 shadow-lg border border-white/10 max-w-[220px]"
              >
                Discover top-performing Facebook & TikTok ads for your products.
                We&apos;re building this next.
                <Tooltip.Arrow className="fill-black/90" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </nav>

        <div className="px-2 py-3 border-t border-[#2A2A40] space-y-3">
          {renderNavLinks(footerLinks)}
          <SidebarBillingSummary onOpenAddons={() => setAddonsOpen(true)} />
        </div>
      </aside>

      {/* Mobile Toggle — placed below promo + header */}
      <div
        className="md:hidden fixed left-4"
        style={{ top: `calc(${TOP_OFFSET} + 8px)`, zIndex: Z_MOBILE_TOGGLE }} // ✅ below modals
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded-md bg-[#FC2783] text-white shadow"
          aria-label="Toggle Menu"
          type="button"
        >
          <Menu size={24} />
        </button>
      </div>

      {/* Mobile Sidebar */}
      {isOpen && (
        <aside
          className="fixed inset-0 bg-black bg-opacity-50 md:hidden"
          style={{ zIndex: Z_MOBILE_SIDEBAR }} // ✅ below modals
        >
          <div
            className="fixed left-0 w-64 bg-[#15152B] text-white p-6 overflow-y-auto"
            style={{
              top: TOP_OFFSET,
              bottom: 0,
              zIndex: Z_MOBILE_SIDEBAR + 1, // keeps panel above its own backdrop
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <div />
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Close Menu"
                className="text-white hover:text-white"
                type="button"
              >
                ✕
              </button>
            </div>
            <nav className="space-y-1">
              <NavLink
                to="/generate"
                end
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  [
                    "block px-4 py-2 rounded-lg font-semibold transition-colors duration-300.ease-out w-full cursor-pointer overflow-hidden",
                    isActive
                      ? "bg-pink-600 text-white"
                      : "bg-[#FC2783] text-white hover:bg.white hover:text-[#FC2783]",
                  ].join(" ")
                }
              >
                Generate
              </NavLink>

              {renderNavLinks(navLinks)}

              <div
                className={[
                  "mt-1 group px-4 py-2 w-full cursor-not-allowed",
                  "text.white/40 hover:text-[#FC2783]",
                  "transition-all duration-300 ease-out hover:-translate-x-[2px] hover:drop-shadow-[0_0_10px_rgba(37,99,235,0.45)]",
                ].join(" ")}
                aria-disabled="true"
              >
                <div className="flex items-center gap-3">
                  <Sparkles
                    size={18}
                    className="opacity-80 group-hover:translate-x-[2px] transition-transform duration-300 ease-out"
                  />
                  <span className="font-semibold">Winning ads</span>
                </div>
                <div className="mt-1.5 ml-7 inline-flex items-center gap-1.5 rounded-full border border-blue-400/70 bg-blue-500/15 px-2.5 py-0.5 text-[10px] text-blue-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  <span>Coming soon</span>
                </div>
              </div>
            </nav>

            <div className="mt-6 border-t border-[#2A2A40] pt-2 space-y-4">
              {renderNavLinks(footerLinks)}
              <SidebarBillingSummary onOpenAddons={() => setAddonsOpen(true)} />
            </div>
          </div>
        </aside>
      )}

      <SidebarAddonsModal open={addonsOpen} onClose={() => setAddonsOpen(false)} />

      {/* Main Content — push below promo + header */}
      <main
        className="flex-1 p-6 overflow-auto bg-[#0F0F1B] rounded-2xl md:rounded-none"
        style={{ paddingTop: TOP_OFFSET }}
      >
        {children}
      </main>
    </div>
  );
}
